import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X } from "lucide-react";
import { ImageErrorBoundary } from "@/components/ImageErrorBoundary";
import { UnsplashImagePickerModal } from "../UnsplashImagePickerModal";
import { BlogPostFormData } from "@/types/blog";

interface MetadataTabProps {
  formData: BlogPostFormData;
  onChange: (field: string, value: any) => void;
  isLoading?: boolean;
}

export const MetadataTab: React.FC<MetadataTabProps> = ({
  formData,
  onChange,
  isLoading = false
}) => {
  const [newCategory, setNewCategory] = useState("");

  // Handle image selection from Unsplash
  const handleUnsplashImageSelect = (imageData: {
    url: string;
    description: string;
    attribution: {
      photographerName: string;
      photographerUsername: string;
      photographerUrl: string;
      unsplashUrl: string;
    };
    width: number;
    height: number;
  }) => {
    // Update the image URL
    onChange("image_url", imageData.url);
    
    // Store the Unsplash attribution data in metadata
    onChange("metadata", {
      ...formData.metadata,
      unsplash_attribution: {
        photographerName: imageData.attribution.photographerName,
        photographerUsername: imageData.attribution.photographerUsername,
        photographerUrl: imageData.attribution.photographerUrl,
        unsplashUrl: imageData.attribution.unsplashUrl,
      }
    });
  };

  // Handle removing the image
  const handleClearImage = () => {
    onChange("image_url", "");
    
    // Remove Unsplash attribution if it exists
    if (formData.metadata?.unsplash_attribution) {
      const newMetadata = { ...formData.metadata };
      delete newMetadata.unsplash_attribution;
      onChange("metadata", newMetadata);
    }
  };

  // Check if the current image is from Unsplash
  const isUnsplashImage = Boolean(formData.metadata?.unsplash_attribution);

  const handleCategoryAdd = () => {
    if (newCategory.trim() && formData.metadata) {
      const updatedCategories = [...(formData.metadata.categories || []), newCategory.trim()];
      onChange("metadata", { ...formData.metadata, categories: updatedCategories });
      setNewCategory("");
    }
  };

  const handleCategoryRemove = (categoryToRemove: string) => {
    if (formData.metadata) {
      const updatedCategories = (formData.metadata.categories || []).filter(
        (category) => category !== categoryToRemove
      );
      onChange("metadata", { ...formData.metadata, categories: updatedCategories });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="featured-image">Featured Image</Label>
        <div className="space-y-4">
          {formData.image_url ? (
            <div className="relative">
              <ImageErrorBoundary 
                fallbackUrl="/placeholder.svg"
                className="w-full h-48 rounded-md overflow-hidden"
              >
                <img 
                  src={formData.image_url} 
                  alt="Featured" 
                  className="w-full h-48 object-cover rounded-md"
                />
              </ImageErrorBoundary>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={handleClearImage}
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
              
              {isUnsplashImage && formData.metadata?.unsplash_attribution && (
                <div className="text-xs text-slate-500 mt-2">
                  Photo by{" "}
                  <a 
                    href={formData.metadata.unsplash_attribution.photographerUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-slate-700"
                  >
                    {formData.metadata.unsplash_attribution.photographerName}
                  </a>{" "}
                  on{" "}
                  <a 
                    href={formData.metadata.unsplash_attribution.unsplashUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-slate-700"
                  >
                    Unsplash
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                id="image_url"
                placeholder="Image URL"
                value={formData.image_url}
                onChange={(e) => onChange("image_url", e.target.value)}
                disabled={isLoading}
              />
              
              <UnsplashImagePickerModal 
                onImageSelect={handleUnsplashImageSelect}
                buttonLabel="Choose from Unsplash"
              />
            </div>
          )}
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="author">Author</Label>
        <Input
          id="author"
          placeholder="Author name"
          value={formData.metadata?.author || ""}
          onChange={(e) => {
            const newMetadata = { ...formData.metadata, author: e.target.value };
            onChange("metadata", newMetadata);
          }}
          disabled={isLoading}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Categories</Label>
        <div className="flex space-x-2">
          <Input
            type="text"
            placeholder="New category"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            disabled={isLoading}
          />
          <Button type="button" onClick={handleCategoryAdd} disabled={isLoading}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(formData.metadata?.categories || []).map((category) => (
            <Button
              key={category}
              variant="secondary"
              size="sm"
              onClick={() => handleCategoryRemove(category)}
              disabled={isLoading}
            >
              {category}
              <X className="ml-2 h-4 w-4" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
